import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import { VideoThumbnailService } from "src/app/shared/services/video-thumbnail.service";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import Swal from "sweetalert2";
import { Subscription } from "rxjs";

@Component({
  selector: "app-tab-videos",
  templateUrl: "./tab-videos.component.html",
  styleUrls: ["./tab-videos.component.scss"],
})
export class TabVideosComponent implements OnChanges, OnDestroy {
  // Constants for limits
  private readonly MAX_VIDEOS_PER_SECTION = 30;
  private readonly MAX_VIDEO_SIZE_MB = 5120; // 5GB
  private readonly MAX_SUBSECTIONS_PER_SECTION = 12;

  // Track pending uploads per section
  private pendingUploadsPerSection: { [sectionIndex: number]: number } = {};

  // Track active upload subscriptions for cancellation
  private activeUploads: Map<string, { subscription: Subscription }> = new Map();

  // Getter methods to expose constants to template
  get maxVideosPerSection(): number {
    return this.MAX_VIDEOS_PER_SECTION;
  }

  get maxVideoSizeMB(): number {
    return this.MAX_VIDEO_SIZE_MB;
  }

  get maxSubsectionsPerSection(): number {
    return this.MAX_SUBSECTIONS_PER_SECTION;
  }

  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};
  @Input() allVideoSectionsExpanded: boolean = false;
  @Input() deleteSection: any[] = [];
  @Input() deleteSubSection: any[] = [];
  @Input() deleteMedia: any[] = [];

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();
  @Output() deleteSectionEvent = new EventEmitter<number>();
  @Output() deleteSubSectionEvent = new EventEmitter<number>();
  @Output() deleteMediaEvent = new EventEmitter<number>();

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private uploadThumbnailService: VideoThumbnailService
  ) {}

  ngOnInit(): void {
    if (this.model.videoSections && this.model.videoSections.length > 0) {
      this.model.videoSections.forEach((section, index) => {
        section.collapsed = false;
        if (section.display === undefined) {
          section.display = true;
        }
        if (section.index === undefined) {
          section.index = index;
        }
        if (section.subSections) {
          section.subSections.forEach((subsection, subIndex) => {
            subsection.collapsed = false;
            if (subsection.display === undefined) {
              subsection.display = true;
            }
            if (subsection.index === undefined) {
              subsection.index = subIndex;
            }
          });
        }
      });

      // Cập nhật checkbox chính dựa trên trạng thái của các sections sau khi khởi tạo
      this.updateMainDisplayCheckbox();
    } else {
      // Nếu không có section nào:
      // - Nếu user đã check displayVideos = true, giữ nguyên để validation có thể báo lỗi
      // - Chỉ untick nếu displayVideos = false (chưa được check)
      if (this.model.displayVideos === false) {
        this.model.displayVideos = false;
      }
      // Nếu displayVideos = true (user đã check), giữ nguyên để validation báo lỗi khi publish
    }
    this.allVideoSectionsExpanded = true;
  }

  checkAndSetVideoDisplay() {
    if (!this.model.displayVideos) {
      if (
        this.model.titleVideoSections ||
        (this.model.videoSections &&
          this.model.videoSections.length > 0 &&
          this.model.videoSections.some(
            section =>
              section.name ||
              (section.subSections &&
                section.subSections.some(
                  sub => sub.title || sub.pageTitle || sub.description || (sub.media && sub.media.length > 0)
                ))
          ))
      ) {
        this.model.displayVideos = true;
      }
    }
    this.modelChange.emit(this.model);
  }

  addVideoSection() {
    if (!this.model.videoSections) {
      this.model.videoSections = [];
    }
    const nextSectionIndex =
      this.model.videoSections.length === 0 ? 0 : Math.max(...this.model.videoSections.map(s => s.index || 0)) + 1;
    const inheritDisplay = !!this.model.displayVideos;
    const newSection = {
      name: "",
      collapsed: false,
      display: inheritDisplay,
      index: nextSectionIndex,
      subSections: [
        {
          title: "",
          pageTitle: "",
          description: "",
          media: [],
          collapsed: false,
          display: inheritDisplay,
          index: 0,
        },
      ],
    };
    this.model.videoSections.push(newSection);
    // this.allVideoSectionsExpanded = true;
    // this.model.videoSections.forEach(section => {
    //   section.collapsed = false;
    //   if (section.subSections) {
    //     section.subSections.forEach(subsection => {
    //       subsection.collapsed = false;
    //     });
    //   }
    // });

    // Cập nhật checkbox chính dựa trên trạng thái của các sections
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  deleteVideoSection(sectionIndex: number) {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the entire section and all its content.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const deleSection = this.model.videoSections[sectionIndex];
        if (deleSection && deleSection.id) {
          this.deleteSection.push(deleSection.id);
          this.deleteSectionEvent.emit(deleSection.id);
        }
        this.model.videoSections.splice(sectionIndex, 1);
        this.modelChange.emit(this.model);
      }
    });
  }

  addVideoSubsection(sectionIndex: number) {
    const section = this.model.videoSections[sectionIndex];
    if (section.subSections.length >= this.MAX_SUBSECTIONS_PER_SECTION) {
      this.toastr.error(`Maximum ${this.MAX_SUBSECTIONS_PER_SECTION} sub-sections allowed per section`);
      return;
    }

    // if (this.isVideoSectionAtMaxCapacity(sectionIndex)) {
    //   const currentVideos = this.getVideoCountInSection(sectionIndex);
    //   const pendingUploads = this.pendingUploadsPerSection[sectionIndex] || 0;
    //   const totalCount = currentVideos + pendingUploads;

    //   this.toastr.error(
    //     `Maximum ${this.MAX_VIDEOS_PER_SECTION} videos per section reached. ` +
    //     `Current total: ${totalCount} (${currentVideos} uploaded + ${pendingUploads} pending). ` +
    //     `Cannot add more sub-sections.`
    //   );
    //   return;
    // }
    const nextSubsectionIndex =
      section.subSections.length === 0 ? 0 : Math.max(...section.subSections.map(s => s.index || 0)) + 1;

    section.subSections.push({
      title: "",
      pageTitle: "",
      description: "",
      media: [],
      collapsed: false,
      display: !!section.display,
      index: nextSubsectionIndex,
    });

    // Cập nhật checkbox chính dựa trên trạng thái của các sections
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  deleteVideoSubsection(sectionIndex: number, subIndex: number) {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the subsection and all its content.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const deleSubSection = this.model.videoSections[sectionIndex].subSections[subIndex];
        if (deleSubSection && deleSubSection.id) {
          this.deleteSubSection.push(deleSubSection.id);
          this.deleteSubSectionEvent.emit(deleSubSection.id);
        }
        this.model.videoSections[sectionIndex].subSections.splice(subIndex, 1);
        this.modelChange.emit(this.model);
      }
    });
  }

  toggleVideoSectionCollapse(section: any) {
    section.collapsed = !section.collapsed;
  }

  toggleVideoSubsectionCollapse(subsection: any) {
    subsection.collapsed = !subsection.collapsed;
  }

  onDisplayVideosChange(value: boolean) {
    this.model.displayVideos = value;
    // Tick/untick tất cả sections và subsections
    if (this.model.videoSections && this.model.videoSections.length > 0) {
      this.model.videoSections.forEach(section => {
        section.display = value;
        if (section.subSections) {
          section.subSections.forEach(subsection => {
            subsection.display = value;
          });
        }
      });
    }
    this.modelChange.emit(this.model);
  }

  onSectionDisplayChange(sectionIndex: number, value: boolean) {
    const section = this.model.videoSections[sectionIndex];
    if (section) {
      section.display = value;

      // Tick/untick tất cả subsection trong section này
      if (section.subSections) {
        section.subSections.forEach(subsection => {
          subsection.display = value;
        });
      }

      // Tự động tick/untick checkbox chính dựa trên trạng thái của các sections
      const oldDisplayVideos = this.model.displayVideos;
      this.updateMainDisplayCheckbox();

      this.modelChange.emit(this.model);
    }
  }

  onSubsectionDisplayChange(sectionIndex: number, subIndex: number, value: boolean) {
    const section = this.model.videoSections[sectionIndex];
    if (section && section.subSections && section.subSections[subIndex]) {
      section.subSections[subIndex].display = value;

      // Nếu subsection được chọn, tự động chọn section
      if (value) {
        section.display = true;
      } else {
        // Nếu subsection bị unselect, kiểm tra xem còn subsection nào được chọn không
        const hasAnySubsectionSelected = section.subSections.some(sub => sub.display === true);
        // Nếu không còn subsection nào được chọn, unselect section
        if (!hasAnySubsectionSelected) {
          section.display = false;
        }
      }

      // Tự động tick/untick checkbox chính dựa trên trạng thái của các sections
      const oldDisplayVideos = this.model.displayVideos;
      this.updateMainDisplayCheckbox();

      this.modelChange.emit(this.model);
    }
  }

  /**
   * Cập nhật checkbox chính (displayVideos) dựa trên trạng thái của các sections
   * - Nếu có ít nhất 1 section được tick → tick checkbox chính
   * - Nếu tất cả sections đều untick → untick checkbox chính
   */
  private updateMainDisplayCheckbox() {
    if (!this.model.videoSections || this.model.videoSections.length === 0) {
      // Nếu không có section nào:
      // - Nếu user đã check displayVideos = true, giữ nguyên để validation có thể báo lỗi
      // - Chỉ untick nếu displayVideos = false (chưa được check)
      if (this.model.displayVideos === false) {
        this.model.displayVideos = false;
      }
      // Nếu displayVideos = true (user đã check), giữ nguyên để validation báo lỗi khi publish
      return;
    }

    // Kiểm tra xem có ít nhất 1 section được tick không
    const hasAnySectionSelected = this.model.videoSections.some(section => section.display === true);

    // Cập nhật checkbox chính - chỉ thay đổi nếu giá trị khác
    if (this.model.displayVideos !== hasAnySectionSelected) {
      this.model.displayVideos = hasAnySectionSelected;
      // Emit để đảm bảo parent component nhận được thay đổi
      this.modelChange.emit(this.model);
    } else {
    }
  }

  dropSection(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.model.videoSections, event.previousIndex, event.currentIndex);
    // Cập nhật index cho tất cả sections sau khi drag & drop
    // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
    this.model.videoSections.forEach((section, index) => {
      section.index = index;
    });
    this.modelChange.emit(this.model);
  }

  moveSectionUp(sectionIndex: number) {
    if (sectionIndex > 0 && this.model.videoSections && this.model.videoSections.length > 1) {
      // Hoán đổi vị trí section với section phía trên
      const temp = this.model.videoSections[sectionIndex];
      this.model.videoSections[sectionIndex] = this.model.videoSections[sectionIndex - 1];
      this.model.videoSections[sectionIndex - 1] = temp;

      // Cập nhật index cho tất cả sections
      // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
      this.model.videoSections.forEach((section, index) => {
        section.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSectionDown(sectionIndex: number) {
    if (
      sectionIndex < this.model.videoSections.length - 1 &&
      this.model.videoSections &&
      this.model.videoSections.length > 1
    ) {
      // Hoán đổi vị trí section với section phía dưới
      const temp = this.model.videoSections[sectionIndex];
      this.model.videoSections[sectionIndex] = this.model.videoSections[sectionIndex + 1];
      this.model.videoSections[sectionIndex + 1] = temp;

      // Cập nhật index cho tất cả sections
      // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
      this.model.videoSections.forEach((section, index) => {
        section.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSubsectionUp(sectionIndex: number, subIndex: number) {
    const section = this.model.videoSections[sectionIndex];
    if (subIndex > 0 && section && section.subSections && section.subSections.length > 1) {
      // Hoán đổi vị trí subsection với subsection phía trên
      const temp = section.subSections[subIndex];
      section.subSections[subIndex] = section.subSections[subIndex - 1];
      section.subSections[subIndex - 1] = temp;

      // Cập nhật index cho tất cả subsections trong section này
      section.subSections.forEach((subsection, index) => {
        subsection.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSubsectionDown(sectionIndex: number, subIndex: number) {
    const section = this.model.videoSections[sectionIndex];
    if (subIndex < section.subSections.length - 1 && section && section.subSections && section.subSections.length > 1) {
      // Hoán đổi vị trí subsection với subsection phía dưới
      const temp = section.subSections[subIndex];
      section.subSections[subIndex] = section.subSections[subIndex + 1];
      section.subSections[subIndex + 1] = temp;

      // Cập nhật index cho tất cả subsections trong section này
      section.subSections.forEach((subsection, index) => {
        subsection.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  toggleExpandAllVideoSections() {
    this.allVideoSectionsExpanded = !this.allVideoSectionsExpanded;
    this.model.videoSections.forEach(section => {
      section.collapsed = !this.allVideoSectionsExpanded;
      section.subSections.forEach(subsection => {
        subsection.collapsed = !this.allVideoSectionsExpanded;
      });
    });
  }

  async uploadVideosToSubsection(files: FileList, sectionIndex: number, subIndex: number, event?: any) {
    if (!files || files.length === 0) return;

    const subsection = this.model.videoSections[sectionIndex].subSections[subIndex];
    if (!subsection.media) {
      subsection.media = [];
    }

    // Check current total including pending uploads
    const currentTotalCount = this.getTotalVideoCountInSection(sectionIndex);

    if (currentTotalCount + files.length > this.MAX_VIDEOS_PER_SECTION) {
      this.toastr.error(
        `Maximum ${this.MAX_VIDEOS_PER_SECTION} videos are allowed per section. You cannot add more videos.`
      );
      return;
    }

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const acceptedVideoTypes = [
        "video/mp4",
        "video/avi",
        "video/x-msvideo",
        "video/quicktime",
        "video/x-ms-wmv",
        "video/x-flv",
        "video/x-matroska",
        "video/mkv",
        "video/webm",
      ];
      if (!acceptedVideoTypes.includes(file.type.toLowerCase())) {
        this.toastr.error(
          `File ${file.name} format not supported. Accepted formats: MP4, AVI, MOV, WMV, FLV, MKV, WEBM`
        );
        continue;
      }

      if (file.size / 1024 / 1024 >= this.MAX_VIDEO_SIZE_MB) {
        this.toastr.error(
          `File ${file.name} size is ${this.formatFileSize(file.size)}. Upload service currently supports maximum 2GB files. Please compress your video or use a smaller file.`
        );
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      return;
    }

    validFiles.sort((a, b) => a.name.localeCompare(b.name));

    // Add to pending uploads counter BEFORE starting uploads
    this.addPendingUploads(sectionIndex, validFiles.length);

    for (let index = 0; index < validFiles.length; index++) {
      const file = validFiles[index];

      try {
        const timestamp = Date.now();
        const uploadKey = this.generateUploadKey(
          "video",
          file.name,
          `section_${sectionIndex}_sub_${subIndex}_${timestamp}_${index}`
        );
        const tempId = `temp_${timestamp}_${index}_${Math.random().toString(36).substr(2, 9)}`;

        this.setUploadState(uploadKey, true);

        const mediaItem = {
          fileName: file.name,
          url: null,
          size: file.size,
          type: "video",
          uploadKey: uploadKey,
          tempId: tempId,
          thumbnail: null,
          needsThumbnailRetry: false,
          thumbnailProcessing: false,
          isPending: true, // Mark as pending
        };

        subsection.media.push(mediaItem);

        // Run thumbnail generation in parallel; do not block video upload.
        const thumbnailPromise = this.generateSingleThumbnail(
          file,
          uploadKey,
          mediaItem,
          subsection,
          "digital/videos",
          this.MAX_VIDEO_SIZE_MB
        ).catch(error => {
          console.error(`Thumbnail generation failed for ${file.name}, continue upload:`, error);
          return null;
        });

        await this.uploadSingleFileToSubsection(
          file,
          uploadKey,
          mediaItem,
          subsection,
          "digital/videos",
          this.MAX_VIDEO_SIZE_MB,
          sectionIndex
        );

        // Do not block next upload by thumbnail completion.
        void thumbnailPromise;
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        // Remove from pending counter if upload fails
        this.removePendingUpload(sectionIndex);
      }
    }

    // Sort toàn bộ media theo fileName
    if (subsection.media && subsection.media.length > 1) {
      subsection.media.sort((a, b) => (a.fileName || "").localeCompare(b.fileName || ""));
    }

    this.checkAndSetVideoDisplay();

    // Reset input để lần sau chọn cùng file vẫn trigger
    if (event && event.target) {
      event.target.value = "";
    }
  }

  private generateSingleThumbnail(
    file: File,
    uploadKey: string,
    mediaItem: any,
    subsection: any,
    s3Path: string,
    maxSizeMB: number
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const thumbnailKey = `${uploadKey}_thumbnail`;
      const subscription = this.uploadThumbnailService.generateThumbnailFromVideo(file, s3Path, maxSizeMB).subscribe(
        (response: any) => {
          if (!response) {
            return;
          }
          const mediaIndex = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);
          if (mediaIndex === -1) {
            return;
          }

          if (response.type === "preview" && response.url) {
            subsection.media[mediaIndex] = {
              ...subsection.media[mediaIndex],
              thumbnail: response.url,
              needsThumbnailRetry: false,
              thumbnailProcessing: true,
            };
            subsection.media = [...subsection.media];
            this.modelChange.emit(this.model);
            return;
          }

          if (response.type === "complete") {
            const prev = subsection.media[mediaIndex];
            const nextThumb =
              response.url && String(response.url).trim() !== "" ? response.url : prev.thumbnail || null;
            const merged = { ...prev, thumbnail: nextThumb };
            subsection.media[mediaIndex] = {
              ...merged,
              thumbnailProcessing: false,
              needsThumbnailRetry: !this.hasDisplayableThumbnail(merged),
            };
            subsection.media = [...subsection.media];
            this.activeUploads.delete(thumbnailKey);
            this.modelChange.emit(this.model);
            resolve({ url: response.url });
          }
        },
        error => {
          console.error(`Thumbnail generation failed for ${file.name}:`, error);
          const mediaIndex = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);
          if (mediaIndex !== -1) {
            subsection.media[mediaIndex] = {
              ...subsection.media[mediaIndex],
              thumbnailProcessing: false,
              needsThumbnailRetry: true,
            };
            subsection.media = [...subsection.media];
            this.modelChange.emit(this.model);
          }
          this.activeUploads.delete(thumbnailKey);
          reject(error);
        }
      );
      this.activeUploads.set(thumbnailKey, { subscription });
    });
  }

  async uploadSingleFileToSubsection(
    file: File,
    uploadKey: string,
    mediaItem: any,
    subsection: any,
    folder: string,
    maxSizeMB: number,
    sectionIndex: number
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      // Create a custom observable wrapper to capture XHR for cancellation
      const uploadObservable = this.uploadService.uploadFileToS3(folder, file, maxSizeMB);

      // Intercept the observable to capture XHR if possible
      // Note: Since XHR is created inside the service, we'll track subscription
      // and handle cleanup in ngOnDestroy
      const subscription = uploadObservable.subscribe({
        next: (response: any) => {
          if (response && response.type === "progress") {
            this.setUploadProgress(uploadKey, response.percent);
            return;
          }

          if (response && response.type === "complete") {
            this.setUploadState(uploadKey, false);

            const mediaIndex = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);

            if (mediaIndex !== -1) {
              subsection.media[mediaIndex] = {
                ...subsection.media[mediaIndex],
                url: response.url,
                uploadKey: null,
                isPending: false, // Remove pending status
              };
              delete subsection.media[mediaIndex].tempId;

              // Remove from pending counter on successful upload
              this.removePendingUpload(sectionIndex);

              this.toastr.success(`Video ${file.name} uploaded successfully`);
              this.cleanupUpload(uploadKey);
              this.activeUploads.delete(uploadKey);
              this.modelChange.emit(this.model);
              resolve(response);
            } else {
              console.error(`Could not find media item with tempId: ${mediaItem.tempId}`);
              this.removePendingUpload(sectionIndex);
              this.activeUploads.delete(uploadKey);
              reject(new Error("Media item not found after upload"));
            }
          }
        },
        error: error => {
          this.setUploadState(uploadKey, false);

          const itemIndex = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);
          if (itemIndex > -1) {
            subsection.media.splice(itemIndex, 1);
          }

          // Remove from pending counter on failed upload
          this.removePendingUpload(sectionIndex);

          this.toastr.error(`Failed to upload ${file.name}: ${error.message || error}`);
          console.error("Upload error:", error);
          this.cleanupUpload(uploadKey);
          this.activeUploads.delete(uploadKey);
          reject(error);
        },
      });

      // Store subscription for cancellation
      this.activeUploads.set(uploadKey, { subscription });
    });
  }

  removeVideoFromSubsection(sectionIndex: number, subIndex: number, mediaIndex: number) {
    const subsection = this.model.videoSections[sectionIndex].subSections[subIndex];
    const mediaId = Number(subsection.media[mediaIndex] && subsection.media[mediaIndex].id);
    if (Number.isFinite(mediaId) && mediaId > 0) {
      this.deleteMedia.push(mediaId);
      this.deleteMediaEvent.emit(mediaId);
    }
    subsection.media.splice(mediaIndex, 1);
    this.modelChange.emit(this.model);
  }

  getVideoUploadText(subsection: any): string {
    if (!subsection.media || subsection.media.length === 0) {
      return "Select Videos";
    }
    const count = subsection.media.length;
    return count === 1 ? "1 video selected" : `${count} videos selected`;
  }

  deleteAllVideosFromSubsection(sectionIndex: number, subIndex: number) {
    if (
      !this.model.videoSections ||
      !this.model.videoSections[sectionIndex] ||
      !this.model.videoSections[sectionIndex].subSections ||
      !this.model.videoSections[sectionIndex].subSections[subIndex]
    ) {
      console.error("Invalid section or subsection index for delete all videos");
      return;
    }

    const subsection = this.model.videoSections[sectionIndex].subSections[subIndex];

    if (!subsection.media || subsection.media.length === 0) {
      this.toastr.info("No videos to delete");
      return;
    }

    const videoCount = subsection.media.length;

    Swal.fire({
      title: "Are you sure?",
      text: `This will delete all ${videoCount} video(s) from this sub-section.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const mediaIdsToDelete = subsection.media
          .map(media => Number(media && media.id))
          .filter(id => Number.isFinite(id) && id > 0);
        if (mediaIdsToDelete.length > 0) {
          this.deleteMedia = this.deleteMedia.concat(mediaIdsToDelete);
          mediaIdsToDelete.forEach(id => this.deleteMediaEvent.emit(id));
        }
        subsection.media = [];
        this.toastr.success(`All ${videoCount} video(s) deleted successfully`);
        this.modelChange.emit(this.model);
      }
    });
  }

  getVideoCountInSection(sectionIndex: number): number {
    if (!this.model.videoSections || !this.model.videoSections[sectionIndex]) {
      return 0;
    }

    let totalVideos = 0;
    const section = this.model.videoSections[sectionIndex];

    if (section.subSections) {
      section.subSections.forEach(subsection => {
        if (subsection.media && subsection.media.length) {
          totalVideos += subsection.media.length;
        }
      });
    }

    return totalVideos;
  }

  /**
   * Get total count including pending uploads
   */
  getTotalVideoCountInSection(sectionIndex: number): number {
    const currentVideos = this.getVideoCountInSection(sectionIndex);
    const pendingUploads = this.pendingUploadsPerSection[sectionIndex] || 0;
    return currentVideos + pendingUploads;
  }

  /**
   * Initialize pending uploads counter for section
   */
  private initializePendingCounter(sectionIndex: number): void {
    if (this.pendingUploadsPerSection[sectionIndex] === undefined) {
      this.pendingUploadsPerSection[sectionIndex] = 0;
    }
  }

  /**
   * Add to pending uploads counter
   */
  private addPendingUploads(sectionIndex: number, count: number): void {
    this.initializePendingCounter(sectionIndex);
    this.pendingUploadsPerSection[sectionIndex] += count;
  }

  /**
   * Remove from pending uploads counter
   */
  private removePendingUpload(sectionIndex: number): void {
    this.initializePendingCounter(sectionIndex);
    if (this.pendingUploadsPerSection[sectionIndex] > 0) {
      this.pendingUploadsPerSection[sectionIndex]--;
    }
  }

  /**
   * Get pending uploads count for section (for template use)
   */
  getPendingUploadsInSection(sectionIndex: number): number {
    return this.pendingUploadsPerSection[sectionIndex] || 0;
  }

  isVideoSectionAtMaxCapacity(sectionIndex: number): boolean {
    return this.getTotalVideoCountInSection(sectionIndex) >= this.MAX_VIDEOS_PER_SECTION;
  }

  validateVideoSectionLimits(): string[] {
    const errors: string[] = [];
    if (this.model.videoSections) {
      this.model.videoSections.forEach((section: any, index: number) => {
        const totalVideoCount = this.getTotalVideoCountInSection(index);
        const currentVideos = this.getVideoCountInSection(index);
        const pendingUploads = this.pendingUploadsPerSection[index] || 0;

        if (totalVideoCount > this.MAX_VIDEOS_PER_SECTION) {
          let errorMessage = `Section ${index + 1}: ${totalVideoCount}/${this.MAX_VIDEOS_PER_SECTION} videos (Max exceeded)`;
          if (pendingUploads > 0) {
            errorMessage += ` - ${currentVideos} uploaded + ${pendingUploads} pending`;
          }
          errors.push(errorMessage);
        }
      });
    }
    return errors;
  }

  showVideoLimitErrors(): void {
    const errors = this.validateVideoSectionLimits();
    if (errors.length > 0) {
      errors.forEach(error => {
        this.toastr.error(error, "Video Limit Exceeded");
      });
    }
  }

  public validateAndShowErrors(): boolean {
    const errors = this.validateVideoSectionLimits();
    if (errors.length > 0) {
      errors.forEach(error => {
        this.toastr.error(error, "Cannot Submit - Video Limit Exceeded");
      });
      return false;
    }
    return true;
  }

  public hasVideoLimitErrors(): boolean {
    return this.validateVideoSectionLimits().length > 0;
  }

  ngOnChanges(): void {
    if (this.model && this.model.videoSections) {
      // Ensure display field has default value
      this.model.videoSections.forEach((section, index) => {
        if (section.display === undefined) {
          section.display = true;
        }
        if (section.index === undefined) {
          section.index = index;
        }
        if (section.subSections) {
          section.subSections.forEach((subsection, subIndex) => {
            if (subsection.display === undefined) {
              subsection.display = true;
            }
            if (subsection.index === undefined) {
              subsection.index = subIndex;
            }
          });
        }
      });

      const errors = this.validateVideoSectionLimits();
      if (errors.length > 0) {
        console.warn("Video section limits exceeded:", errors);
      }
    }
  }

  onPreviewClick(media: any, type: string) {
    if (media.url) {
      window.open(media.url, "_blank");
    }
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }
  scrollToVideoSection(sectionIndex: number): void {
    const el = document.getElementById(`video-section-${sectionIndex}`);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // Helper methods
  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  private generateUploadKey(prefix: string, fileName: string, suffix?: string): string {
    const base = `${prefix}_${Date.now()}_${fileName}`;
    return suffix ? `${base}_${suffix}` : base;
  }

  private setUploadState(key: string, isUploading: boolean) {
    this.uploadStates[key] = isUploading;
  }

  private setUploadProgress(key: string, percent: number) {
    this.uploadProgress[key] = percent;
  }

  isUploadInProgress(key: string): boolean {
    return this.uploadStates[key] || false;
  }

  getProgressByKey(key: string): number {
    return this.uploadProgress[key] || 0;
  }

  private cleanupUpload(key: string) {
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  private hasDisplayableThumbnail(media: any): boolean {
    const thumbnail = media && media.thumbnail;
    if (typeof thumbnail !== "string") {
      return false;
    }
    const normalized = thumbnail.trim();
    return normalized.length > 0 && normalized !== "data:,";
  }

  private isNewUploadedVideo(media: any): boolean {
    if (!media || String(media.type || "").toLowerCase() !== "video") {
      return false;
    }
    const mediaId = Number(media.id);
    return !Number.isFinite(mediaId) || mediaId <= 0;
  }

  private shouldWarnMissingThumbnail(media: any): boolean {
    if (!this.isNewUploadedVideo(media)) {
      return false;
    }
    if (!media.url || media.isPending === true) {
      return false;
    }
    if (media.thumbnailProcessing === true) {
      return false;
    }
    if (this.hasDisplayableThumbnail(media)) {
      return false;
    }
    return media.needsThumbnailRetry === true;
  }

  getMissingThumbnailCountForSubsection(subsection: any): number {
    if (!subsection || !subsection.media) {
      return 0;
    }
    return (subsection.media || []).filter((media: any) => this.shouldWarnMissingThumbnail(media)).length;
  }

  getMissingThumbnailWarningMessageForSubsection(subsection: any): string {
    const count = this.getMissingThumbnailCountForSubsection(subsection);
    if (count <= 0) {
      return "";
    }
    return `${count} newly uploaded video(s) failed thumbnail generation and need retry. If not retried, these videos may be shown without thumbnail.`;
  }

  /**
   * Handle multi-upload completion
   */
  onMultiUploadComplete(
    event: { file: File; url: string; fileId: string; mediaItem: any },
    sectionIndex: number
  ): void {
    // Media item is already updated in multi-upload component
    // Just need to remove from pending counter and emit model change
    this.removePendingUpload(sectionIndex);
    this.modelChange.emit(this.model);
    this.checkAndSetVideoDisplay();
  }

  /**
   * Handle multi-upload error
   */
  onMultiUploadError(event: { file: File; error: string; fileId: string }): void {
    // Suppress error messages if upload was cancelled (e.g., component destroyed)
    if (event.error && (event.error.includes("cancelled") || event.error.includes("aborted"))) {
      // Silently handle cancellation - don't log errors
      return;
    }

    // Find the section and subsection that contains this upload
    // Error is already handled in multi-upload component, just log it
    console.error(`Upload error for ${event.file.name}:`, event.error);

    // Remove from pending counter if needed
    // Note: We need to find which section this belongs to
    // For now, multi-upload component handles its own error display
  }

  /**
   * Handle multi-upload progress
   */
  onMultiUploadProgress(progress: any): void {
    // Progress is handled by multi-upload component
    // This is just for tracking if needed
    if (progress.fileId) {
      // You can track progress here if needed
    }
  }

  /**
   * Cleanup when component is destroyed - cancel all active uploads
   */
  ngOnDestroy(): void {
    // Cancel all active uploads
    this.activeUploads.forEach((upload, uploadKey) => {
      // Unsubscribe from observable - this will trigger teardown logic in service
      // which will abort the XMLHttpRequest
      if (upload.subscription && !upload.subscription.closed) {
        upload.subscription.unsubscribe();
      }

      // Cleanup upload tracking
      this.cleanupUpload(uploadKey);

      // Remove pending media items that are still uploading
      if (this.model && this.model.videoSections) {
        this.model.videoSections.forEach((section, sectionIndex) => {
          if (section.subSections) {
            section.subSections.forEach(subsection => {
              if (subsection.media) {
                const pendingMediaIndex = subsection.media.findIndex(
                  (media: any) => media.uploadKey === uploadKey || (media.isPending === true && !media.url)
                );
                if (pendingMediaIndex > -1) {
                  const mediaItem = subsection.media[pendingMediaIndex];
                  // Only remove if it's still pending (no URL yet)
                  if (!mediaItem.url || mediaItem.isPending) {
                    subsection.media.splice(pendingMediaIndex, 1);
                    this.removePendingUpload(sectionIndex);
                  }
                }
              }
            });
          }
        });
      }
    });

    // Clear all active uploads
    this.activeUploads.clear();

    // Clear pending uploads counters
    this.pendingUploadsPerSection = {};
  }
}
